import { siteConfig } from "@/lib/config";
import { type ClassValue, clsx } from "clsx";
import { Metadata } from "next";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function absoluteUrl(path: string) {
  return `${siteConfig.url}${path}`;
}

// Logo is centered so Google's square thumbnail crop still shows the full wordmark.
export const OG_IMAGE = absoluteUrl("/og-image.png");

export function constructMetadata({
  title = siteConfig.name,
  description = siteConfig.description,
  image = OG_IMAGE,
  ...props
}: {
  title?: string;
  description?: string;
  image?: string;
  [key: string]: Metadata[keyof Metadata];
}): Metadata {
  return {
    title: {
      template: "%s | " + siteConfig.name,
      default: title,
    },
    description: description || siteConfig.description,
    keywords: siteConfig.keywords,
    // No url here: child pages inherit openGraph wholesale, so a url would
    // label every page as the homepage. Pages set their own via pageMetadata.
    openGraph: {
      title,
      description,
      siteName: siteConfig.name,
      images: [
        {
          url: image,
          width: 1200,
          height: 630,
          alt: siteConfig.name,
        },
      ],
      type: "website",
      locale: "en_US",
    },
    metadataBase: new URL(siteConfig.url),
    authors: [
      {
        name: siteConfig.name,
        url: siteConfig.url,
      },
    ],
    ...props,
  };
}

/**
 * Per-page metadata for marketing pages: canonical URL plus Open Graph tags
 * that describe this page rather than inheriting the homepage's.
 * `title` gets the root " | Elementix" template unless `absoluteTitle` is set.
 */
export function pageMetadata({
  title,
  description,
  path,
  absoluteTitle = false,
}: {
  title: string;
  description: string;
  path: string;
  absoluteTitle?: boolean;
}): Metadata {
  const url = path === "/" ? siteConfig.url : absoluteUrl(path);
  const fullTitle = absoluteTitle ? title : `${title} | ${siteConfig.name}`;
  return {
    title: absoluteTitle ? { absolute: title } : title,
    description,
    alternates: { canonical: url },
    openGraph: {
      title: fullTitle,
      description,
      url,
      siteName: siteConfig.name,
      images: [{ url: OG_IMAGE, width: 1200, height: 630, alt: siteConfig.name }],
      type: "website",
      locale: "en_US",
    },
  };
}

export function formatDate(date: string) {
  let currentDate = new Date().getTime();
  if (!date.includes("T")) {
    date = `${date}T00:00:00`;
  }
  let targetDate = new Date(date).getTime();
  let timeDifference = Math.abs(currentDate - targetDate);
  let daysAgo = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  let fullDate = new Date(date).toLocaleString("en-us", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  if (daysAgo < 1) {
    return "Today";
  } else if (daysAgo < 7) {
    return `${fullDate} (${daysAgo}d ago)`;
  } else if (daysAgo < 30) {
    const weeksAgo = Math.floor(daysAgo / 7);
    return `${fullDate} (${weeksAgo}w ago)`;
  } else if (daysAgo < 365) {
    const monthsAgo = Math.floor(daysAgo / 30);
    return `${fullDate} (${monthsAgo}mo ago)`;
  } else {
    const yearsAgo = Math.floor(daysAgo / 365);
    return `${fullDate} (${yearsAgo}y ago)`;
  }
}

// ============================================
// Shared PSEO Utilities
// ============================================

// Helper to convert numeric strings to numbers
export function parseNumeric(value: any): number | null {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? null : num;
}

// Helper to convert strings to Title Case
export function toTitleCase(str: string | null | undefined): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Alias for backwards compatibility
export const toCamelCase = toTitleCase;

// Helper to blur email (everything before @)
export function blurEmail(email: string): { blurred: string; visible: string } {
  const [localPart, domain] = email.split('@');
  if (!domain) return { blurred: '', visible: email };
  return {
    blurred: '8'.repeat(8),
    visible: `@${domain}`,
  };
}

// Helper to blur phone (everything but last 4 digits)
export function blurPhone(phone: string): { blurred: string; visible: string } {
  if (phone.length <= 4) return { blurred: '', visible: phone };
  const lastFour = phone.slice(-4);
  const hiddenLength = phone.length - 4;
  return {
    blurred: '8'.repeat(hiddenLength),
    visible: lastFour,
  };
}

// Helper to format duration in years/months
export function formatDuration(years: number): string {
  if (years < 1) {
    const months = Math.round(years * 12);
    return `${months} ${months === 1 ? 'mo' : 'mos'}`;
  }
  if (years < 2) {
    const months = Math.round(years * 12);
    return `${months} ${months === 1 ? 'mo' : 'mos'}`;
  }
  return `${years.toFixed(1)} yrs`;
}

// Helper to split address at first comma for display
export function splitAddress(address: string): { line1: string; line2: string } {
  const firstCommaIndex = address.indexOf(',');
  if (firstCommaIndex === -1) {
    return { line1: address, line2: '' };
  }
  return {
    line1: address.substring(0, firstCommaIndex + 1),
    line2: address.substring(firstCommaIndex + 1).trim(),
  };
}

// Helper to format a list with proper "and" conjunction (Oxford comma style)
export function formatListWithAnd(items: string[]): string {
  const filtered = items.filter(Boolean);
  if (filtered.length === 0) return '';
  if (filtered.length === 1) return filtered[0];
  if (filtered.length === 2) return `${filtered[0]} and ${filtered[1]}`;
  return `${filtered.slice(0, -1).join(', ')}, and ${filtered[filtered.length - 1]}`;
}

// Helper to convert name to URL-safe slug
export function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Helper to convert URL slug back to name (uppercase)
export function slugToName(slug: string): string {
  return slug.replace(/-/g, ' ').toUpperCase();
}

// Helper to format currency
export function formatCurrency(amount: number | null | undefined): string {
  if (!amount) return "$0";
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  }
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}k`;
  }
  return `$${amount.toFixed(0)}`;
}

// Helper to format numbers with commas
export function formatNumber(num: number | null | undefined): string {
  if (!num) return "0";
  return num.toLocaleString('en-US');
}

// Helper to format date for PSEO pages (MM/DD/YYYY format)
export function formatDateShort(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

// Helper to calculate property status
export function getPropertyStatus(status: string, durationYears: number | null): string {
  if (status === 'sold') return 'Sold';
  if (durationYears) return `Owns - ${formatDuration(durationYears)}`;
  return 'Owns';
}

// Helper to format percentage
export function formatPercentage(value: number | null | undefined): string {
  if (value === null || value === undefined) return "0%";
  return `${Math.round(value)}%`;
}

// State abbreviation to full name mapping
export const STATE_NAMES: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia',
};

export function getStateName(abbrev: string): string {
  return STATE_NAMES[abbrev.toUpperCase()] || abbrev;
}

// Lender type definitions with descriptions (written to flow naturally in a sentence)
export const LENDER_TYPES = [
  {
    value: "Investment Lender",
    label: "Investment Lender",
    description: "Investment lenders provide asset-based financing for real estate investors, including DSCR, fix and flip, and bridge loans."
  },
  {
    value: "Institutional Lender", 
    label: "Institutional Lender",
    description: "Institutional lenders provide large-scale debt for major commercial real estate projects."
  },
  {
    value: "Bank",
    label: "Bank",
    description: "Banks are FDIC-insured depository institutions offering a range of mortgage products."
  },
  {
    value: "Credit Union",
    label: "Credit Union", 
    description: "Credit unions are member-owned, not-for-profit financial cooperatives."
  },
  {
    value: "Mortgage Banker",
    label: "Mortgage Banker",
    description: "Mortgage bankers are residential lenders that fund loans from their balance sheet or warehouse lines."
  },
  {
    value: "Government",
    label: "Government",
    description: "Government-sponsored enterprises (GSEs) directly originate or guarantee loans."
  }
] as const;

export function getLenderTypeDescription(lenderType: string | null | undefined): string | null {
  if (!lenderType) return null;
  const found = LENDER_TYPES.find(t => t.value.toLowerCase() === lenderType.toLowerCase());
  return found?.description || null;
}
